import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Award, TrendingUp, BookOpen, GraduationCap, CalendarRange } from "lucide-react";
import { fmtDate } from "@/lib/format";

const GRADE_LABEL = { "30L": "30L", idoneo: "Idoneo", non_idoneo: "Non idoneo" };
const ALL = "__all__";

const normAY = (v) => (v ? String(v).replace("-", "/") : v);

export default function PortalLibretto() {
  const { data, isLoading } = useQuery({
    queryKey: ["my-booklet"],
    queryFn: async () => (await api.get("/exams/booklet/me")).data,
  });

  const [ay, setAy] = useState(ALL);

  const availableYears = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    (data.grades || []).forEach((g) => {
      const v = normAY(g.year_academic);
      if (v) set.add(v);
    });
    return Array.from(set).sort().reverse();
  }, [data]);

  if (isLoading || !data) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  const { student, grades = [], cfu_earned, count_passed, media_ponderata, media_aritmetica, by_year, by_academic_year = {} } = data;

  // Filtro applicato ai voti visualizzati
  const filteredGrades = ay === ALL
    ? grades
    : grades.filter((g) => normAY(g.year_academic) === ay);

  const passed = filteredGrades.filter((g) => g.is_passed);

  // Aggregati per l'a.a. selezionato
  const ayInfo = ay === ALL ? null : by_academic_year[ay];
  const shownCfu = ay === ALL ? cfu_earned : (ayInfo?.cfu ?? 0);
  const shownCount = ay === ALL ? count_passed : (ayInfo?.count ?? 0);
  const shownMediaPond = ay === ALL ? media_ponderata : ayInfo?.media_ponderata;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="portal-libretto">
      <PageHeader
        title="Il mio libretto"
        subtitle="Voti verbalizzati, CFU maturati e media di carriera."
      />

      {/* Header studente */}
      <Card className="p-6 border border-stone-200 rounded-xl shadow-none mb-6 bg-gradient-to-br from-[#1C352D] to-[#264A3E] text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#E2b8a8]">Studente</div>
            <div className="text-2xl font-semibold mt-1">{student.first_name} {student.last_name}</div>
            <div className="text-[11px] text-stone-300 mt-1 mono">
              {student.matricola} · {student.year_level}° anno · {student.track_type} · {student.academic_year || "—"}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-[#E2b8a8]">CFU {ay === ALL ? "totali" : ay}</div>
              <div className="text-4xl font-semibold text-[#E2725B]" data-testid="libretto-cfu">{shownCfu}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-[#E2b8a8]">Media pond.</div>
              <div className="text-4xl font-semibold" data-testid="libretto-media">{shownMediaPond ?? "—"}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Filtro anno accademico */}
      {availableYears.length > 0 && (
        <div className="mb-5 flex items-center flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs text-stone-500 mr-2">
            <CalendarRange size={14}/>
            <span>Filtra per anno accademico:</span>
          </div>
          <button
            data-testid="ay-filter-all"
            onClick={() => setAy(ALL)}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${ay === ALL ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}
          >
            Tutti gli anni
          </button>
          {availableYears.map((y) => (
            <button
              key={y}
              data-testid={`ay-filter-${y}`}
              onClick={() => setAy(y)}
              className={`px-3 py-1 rounded-full text-xs border transition-colors mono ${ay === y ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}
            >
              {y}
              {by_academic_year[y] && (
                <span className={`ml-1.5 ${ay === y ? "text-[#E2b8a8]" : "text-stone-400"}`}>· {by_academic_year[y].cfu} CFU</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Esami sostenuti" value={shownCount} icon={Award}/>
        <Kpi label="Media aritmetica" value={ay === ALL ? (media_aritmetica ?? "—") : "—"} icon={TrendingUp}/>
        <Kpi label="Anno I · CFU" value={by_year?.["1"]?.cfu ?? 0} icon={BookOpen}/>
        <Kpi label="Anno II · CFU" value={by_year?.["2"]?.cfu ?? 0} icon={GraduationCap}/>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-5 border-b border-stone-100 text-sm font-medium flex items-center justify-between">
          <span>Esami verbalizzati {ay !== ALL && <span className="text-stone-500 font-normal ml-1">· a.a. {ay}</span>}</span>
          <span className="text-xs text-stone-500">{passed.length} voti</span>
        </div>
        {passed.length === 0 ? (
          <div className="py-16 text-center text-stone-400 text-sm">
            {ay === ALL
              ? <>Nessun esame verbalizzato ancora. Prenotati agli appelli aperti dalla sezione &laquo;Appelli&raquo;.</>
              : <>Nessun esame verbalizzato per l'a.a. <b>{ay}</b>.</>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="text-left px-4 py-3">Corso</th>
                  <th className="text-left px-4 py-3">Area</th>
                  <th className="text-left px-4 py-3">Anno</th>
                  <th className="text-left px-4 py-3">A.A.</th>
                  <th className="text-right px-4 py-3">CFU</th>
                  <th className="text-center px-4 py-3">Voto</th>
                  <th className="text-left px-4 py-3">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {passed.map((g, i) => (
                  <tr key={g.id} data-testid={`booklet-row-${g.id}`} className="hover:bg-stone-50 animate-in-row" style={{ animationDelay: `${i * 10}ms` }}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1C352D]">{g.course_name}</div>
                      {g.course_code && <div className="text-[11px] text-stone-500 mono">{g.course_code}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {g.area_name ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-stone-600">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ background: g.area_color }}/>
                          {g.area_name}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600">{g.year_level ? `${g.year_level}°` : "—"}</td>
                    <td className="px-4 py-3 text-xs text-stone-500 mono">{g.year_academic || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium">{g.cfu ?? "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-emerald-100 text-emerald-800 font-semibold rounded-full text-sm">
                        {GRADE_LABEL[g.grade_value] || g.grade_value}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-500">{fmtDate(g.date)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-stone-50 border-t border-stone-200 text-sm font-semibold">
                <tr>
                  <td className="px-4 py-3" colSpan={4}>Totale {ay !== ALL && `a.a. ${ay}`}</td>
                  <td className="px-4 py-3 text-right text-[#E2725B]">{shownCfu} CFU</td>
                  <td className="px-4 py-3 text-center">{shownMediaPond ?? "—"}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Icon size={11}/> {label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </Card>
  );
}
